import {
  ACTIONS_FILTER_LABELS,
  ACTIONS_FILTER_VALUES,
  CONTACT_STATE_LABELS,
  MANAGER_ROLES,
  OVERDUE_DAYS_OPTIONS,
  overdueDaysLabel,
  type ContactState,
} from '@performance-crm/shared';
import { Search, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { useAuth } from '@/lib/auth-context';
import { useCompanyScope } from '@/lib/useCompanyScope';
import { BAND_LABEL, FOLLOWUP_LABEL } from '@/lib/format';
import { useAdvisors, useCatalogs, useProjects } from '@/lib/queries';
import { UNASSIGNED_OPTION } from './leadFilterRules';

const FOLLOWUP_OPTIONS = ['NO_GESTIONADO', 'VENCIDO', 'AL_DIA', 'INACTIVO'];
const STATUS_LABEL: Record<string, string> = { ACTIVO: 'Activos', CERRADO: 'Cerrados' };
/** Barra de filtros compartida por el listado y el kanban. Los valores viven en la URL. */
export function LeadFilters({
  params,
  onChange,
  extra,
}: {
  params: URLSearchParams;
  onChange: (key: string, value: string) => void;
  extra?: React.ReactNode;
}) {
  const { companies, active } = useCompanyScope();
  const { user } = useAuth();
  const companyId = params.get('companyId') ?? active;
  const urlQuery = params.get('q') ?? '';
  const [q, setQ] = useState(urlQuery);
  const [qError, setQError] = useState('');
  // La caja sigue a la URL: al limpiar, al aplicar una vista guardada o al llegar por enlace muestra lo que filtra.
  useEffect(() => {
    setQ(urlQuery);
    setQError('');
  }, [urlQuery]);
  const submitQuery = (value: string) => {
    const text = value.trim();
    if (text.length === 1) {
      setQError('Escribe al menos 2 letras, o 4 dígitos del celular o documento.');
      return;
    }
    setQError('');
    if (text !== urlQuery) onChange('q', text);
  };
  const isManager = !!user?.memberships.some(
    (m) => m.companyId === companyId && MANAGER_ROLES.includes(m.role),
  );
  const { data: projects } = useProjects();
  // Quien gestiona también ve a los asesores dados de baja (grupo «Inactivos») para ubicar y reasignar sus leads.
  const { data: advisors } = useAdvisors(companyId, { includeInactive: isManager });
  const { data: catalogs } = useCatalogs(companyId);
  const visibleProjects = (projects ?? []).filter((p) => !companyId || p.companyId === companyId);
  // Con `includeInactive` la API trae también a quienes hoy tienen otro rol (marketing, administración…): en el
  // filtro solo cuentan los que reciben leads y los dados de baja (que pueden tener leads a su nombre).
  const activeAdvisors = (advisors ?? []).filter((a) => a.active && a.assignable !== false);
  const inactiveAdvisors = (advisors ?? []).filter((a) => !a.active);
  const advisorValue =
    params.get('unassigned') === 'true' ? UNASSIGNED_OPTION : (params.get('advisorId') ?? '');
  // Filtros que llegan por enlace (Equipo → Bolsa) y no tienen control en la barra: se ven como chip y se pueden quitar.
  const chips: Array<{ key: string; label: string }> = [];
  const band = params.get('overdueBand');
  if (band) chips.push({ key: 'overdueBand', label: `Vencidos ${BAND_LABEL[band] ?? band}` });
  const status = params.get('status');
  if (status) chips.push({ key: 'status', label: `Estado: ${STATUS_LABEL[status] ?? status}` });

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        submitQuery(q);
      }}
      className="bg-card grid gap-2 rounded-lg border p-3 md:grid-cols-3 lg:grid-cols-6"
    >
      <div className="relative md:col-span-2">
        <Search className="text-muted-foreground pointer-events-none absolute left-2.5 top-3 h-4 w-4" />
        <Input
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
            if (qError) setQError('');
          }}
          placeholder="Nombre, celular, correo o documento (Enter para buscar)"
          className="pl-8 pr-8"
          aria-label="Buscar"
          aria-invalid={qError ? true : undefined}
        />
        {(q || urlQuery) && (
          <button
            type="button"
            onClick={() => {
              setQ('');
              setQError('');
              if (urlQuery) onChange('q', '');
            }}
            className="text-muted-foreground hover:text-foreground absolute right-2 top-2.5 rounded p-0.5"
            aria-label="Limpiar búsqueda"
            data-testid="search-clear"
          >
            <X className="h-4 w-4" />
          </button>
        )}
        {qError && (
          <p className="text-destructive mt-1 text-xs" role="alert">
            {qError}
          </p>
        )}
      </div>
      {companies.length > 1 && (
        <Select
          value={companyId ?? ''}
          onChange={(e) => onChange('companyId', e.target.value)}
          aria-label="Empresa"
        >
          {companies.map((m) => (
            <option key={m.companyId} value={m.companyId}>
              {m.companyName}
            </option>
          ))}
        </Select>
      )}
      <Select
        value={params.get('projectId') ?? ''}
        onChange={(e) => onChange('projectId', e.target.value)}
        aria-label="Proyecto"
      >
        <option value="">Todos los proyectos</option>
        {visibleProjects.map((p) => (
          <option key={p.id} value={p.id}>
            {p.name}
          </option>
        ))}
      </Select>
      <Select
        value={params.get('followupState') ?? ''}
        onChange={(e) => onChange('followupState', e.target.value)}
        aria-label="Seguimiento"
      >
        <option value="">Cualquier seguimiento</option>
        {FOLLOWUP_OPTIONS.map((s) => (
          <option key={s} value={s}>
            {FOLLOWUP_LABEL[s]}
          </option>
        ))}
      </Select>
      {/* Estado del lead (F3): «Leads activos» del Resumen llega con status=ACTIVO y aquí se ve y se quita. */}
      <Select
        value={params.get('status') ?? ''}
        onChange={(e) => onChange('status', e.target.value)}
        aria-label="Estado del lead"
        data-testid="filter-status"
      >
        <option value="">Activos y cerrados</option>
        <option value="ACTIVO">Solo activos</option>
        <option value="CERRADO">Solo cerrados</option>
      </Select>
      {(!params.get('followupState') || params.get('followupState') === 'VENCIDO') && (
        <Select
          value={params.get('overdueDays') ?? ''}
          onChange={(e) => onChange('overdueDays', e.target.value)}
          aria-label="Días vencidos"
          data-testid="filter-overdue-days"
        >
          <option value="">Días vencidos: todos</option>
          {OVERDUE_DAYS_OPTIONS.map((d) => (
            <option key={d} value={d}>
              {overdueDaysLabel(d)}
            </option>
          ))}
        </Select>
      )}
      <Select
        value={params.get('contactState') ?? ''}
        onChange={(e) => onChange('contactState', e.target.value)}
        aria-label="Contacto"
        data-testid="filter-contact"
      >
        <option value="">Contacto: todos</option>
        {(Object.keys(CONTACT_STATE_LABELS) as ContactState[]).map((c) => (
          <option key={c} value={c}>
            {CONTACT_STATE_LABELS[c]}
          </option>
        ))}
      </Select>
      <Select
        value={params.get('actions') ?? ''}
        onChange={(e) => onChange('actions', e.target.value)}
        aria-label="Gestiones"
        data-testid="filter-actions"
      >
        <option value="">Gestiones: todas</option>
        {ACTIONS_FILTER_VALUES.map((v) => (
          <option key={v} value={v}>
            {ACTIONS_FILTER_LABELS[v]}
          </option>
        ))}
      </Select>
      {advisors && advisors.length > 0 && (
        <Select
          value={advisorValue}
          onChange={(e) => onChange('advisorId', e.target.value)}
          aria-label="Asesor"
          data-testid="filter-advisor"
        >
          <option value="">Cualquier asesor</option>
          <option value={UNASSIGNED_OPTION}>Sin asesor</option>
          {activeAdvisors.map((a) => (
            <option key={a.id} value={a.id}>
              {a.fullName}
            </option>
          ))}
          {inactiveAdvisors.length > 0 && (
            <optgroup label="Inactivos">
              {inactiveAdvisors.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.fullName}
                </option>
              ))}
            </optgroup>
          )}
        </Select>
      )}
      {catalogs && (
        <Select
          value={params.get('mediumCategory') ?? ''}
          onChange={(e) => onChange('mediumCategory', e.target.value)}
          aria-label="Medio"
        >
          <option value="">Cualquier medio</option>
          {catalogs.mediumCategories.map((c) => (
            <option key={c.code} value={c.code}>
              {c.name}
            </option>
          ))}
        </Select>
      )}
      {catalogs && catalogs.tags.length > 0 && (
        <Select
          value={params.get('tagIds') ?? ''}
          onChange={(e) => onChange('tagIds', e.target.value)}
          aria-label="Etiqueta"
        >
          <option value="">Cualquier etiqueta</option>
          {catalogs.tags.map((t) => (
            <option key={t.id} value={t.id}>
              {t.name}
            </option>
          ))}
        </Select>
      )}
      {catalogs?.customFields
        .filter((f) => f.type === 'LISTA' || f.type === 'SI_NO')
        .map((f) => (
          <Select
            key={f.key}
            value={params.get(`cf_${f.key}`) ?? ''}
            onChange={(e) => onChange(`cf_${f.key}`, e.target.value)}
            aria-label={f.label}
          >
            <option value="">{f.label}: cualquiera</option>
            {f.type === 'SI_NO' ? (
              <>
                <option value="true">{f.label}: Sí</option>
                <option value="false">{f.label}: No</option>
              </>
            ) : (
              f.options.map((o) => (
                <option key={o} value={o}>
                  {f.label}: {o}
                </option>
              ))
            )}
          </Select>
        ))}
      {/* Dos campos de fecha no caben en una celda de la rejilla: el rango ocupa dos columnas y los campos se reparten el ancho.
          Las fechas viajan tal como se eligen (AAAA-MM-DD); la API las toma como días completos de la zona de la
          empresa y el «hasta» es inclusivo (ux-listado-03). */}
      <div className="flex min-w-0 items-center gap-1 md:col-span-2" data-testid="captured-range">
        <span className="text-muted-foreground shrink-0 text-xs">Captado</span>
        <Input
          type="date"
          value={params.get('capturedFrom')?.slice(0, 10) ?? ''}
          onChange={(e) => onChange('capturedFrom', e.target.value)}
          aria-label="Captado desde"
          className="h-10 min-w-0 flex-1"
        />
        <span className="text-muted-foreground shrink-0 text-xs">a</span>
        <Input
          type="date"
          value={params.get('capturedTo')?.slice(0, 10) ?? ''}
          onChange={(e) => onChange('capturedTo', e.target.value)}
          aria-label="Captado hasta"
          className="h-10 min-w-0 flex-1"
        />
      </div>
      {extra}
      {chips.length > 0 && (
        <div className="flex flex-wrap items-center gap-1 md:col-span-3 lg:col-span-6">
          {chips.map((c) => (
            <span
              key={c.key}
              className="bg-muted inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs"
              data-testid={`filter-chip-${c.key}`}
            >
              {c.label}
              <button
                type="button"
                onClick={() => onChange(c.key, '')}
                className="text-muted-foreground hover:text-foreground rounded-full"
                aria-label={`Quitar filtro ${c.label}`}
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
        </div>
      )}
    </form>
  );
}
